import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  AlertTriangle, 
  Users, 
  Mail, 
  Phone, 
  LogOut,
  RefreshCw
} from 'lucide-react';

const NoTenantAccess = () => {
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {/* Icon */}
          <div className="mx-auto h-20 w-20 bg-yellow-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="h-10 w-10 text-yellow-600" />
          </div>
          
          {/* Title */}
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            No Tenant Access
          </h2>
          
          {/* Message */}
          <p className="mt-2 text-sm text-gray-600">
            Your account is not currently assigned to any tenant. Please contact your administrator to get access.
          </p>
        </div>

        {/* User Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Your Account Information</h3>
          
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Users className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-sm text-gray-500">@{user?.username}</p>
              </div>
            </div>
            
            {user?.email && (
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-gray-400" />
                <p className="text-sm text-gray-900">{user.email}</p>
              </div>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">
            What to do next:
          </h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Contact your system administrator</li>
            <li>• Request access to a tenant/organization</li>
            <li>• Provide your username: <strong>@{user?.username}</strong></li>
          </ul>
        </div>

        {/* Contact Admin */}
        <div className="bg-gray-100 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
            <Phone className="h-4 w-4 mr-2" />
            Need Help?
          </h4>
          <p className="text-sm text-gray-600">
            Contact your system administrator to assign you to a tenant and grant you access to the CRM system.
          </p>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <button
            onClick={handleRefresh}
            className="flex-1 flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
          
          <button
            onClick={handleLogout}
            className="flex-1 flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </button>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            Once you have been assigned to a tenant, refresh this page or log in again to access the system.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NoTenantAccess;
